(() => {
  const SELECTOR = '[data-trybb-roadmap]';
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  class TrybbRoadmap {
    constructor(root) {
      this.root = root;
      this.lessons = [...root.querySelectorAll('[data-roadmap-lesson]')];
      this.svg = root.querySelector('[data-roadmap-thread-svg]');
      this.lineGroup = root.querySelector('[data-roadmap-thread-lines]');
      this.shadowGroup = root.querySelector('[data-roadmap-thread-shadows]');
      this.waypoints = [...root.querySelectorAll('[data-roadmap-waypoint]')];
      this.finalScene = root.querySelector('.trybb-roadmap__passport');
      this.initialScrollY = window.scrollY || window.pageYOffset || 0;

      this.threadWidth = this.number('threadWidth', 3);
      this.shadowWidth = this.number('threadShadowWidth', 6);
      this.shadowOpacity = this.number('threadShadowOpacity', .4);
      this.revealLead = clamp(this.number('threadRevealLead', 72) / 100, .45, .9);
      this.organicVariation = clamp(this.number('threadOrganic', 8), 0, 24);
      this.threadFollow = clamp(this.number('threadFollow', 18) / 100, .08, .35);
      this.waypointGlow = clamp(this.number('waypointGlow', 55) / 100, 0, 1);
      this.waypointPassedOpacity = clamp(this.number('waypointPassedOpacity', 60) / 100, .2, 1);
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.segments = [];
      this.totalLength = 0;
      this.currentReveal = 0;
      this.targetReveal = 0;
      this.buildFrame = 0;
      this.scrollFrame = 0;
      this.threadFrame = 0;
      this.resizeObserver = null;

      this.init();
    }

    number(key, fallback) {
      return Number.parseFloat(this.root.dataset[key] || String(fallback));
    }

    init() {
      this.root.style.setProperty('--rm-red', this.root.dataset.threadColor || '#B33A2F');
      this.root.style.setProperty('--rm-thread-width', `${this.threadWidth}px`);
      this.root.style.setProperty('--rm-thread-shadow-width', `${this.shadowWidth}px`);
      this.root.style.setProperty('--rm-thread-shadow-opacity', `${this.shadowOpacity}`);
      this.root.style.setProperty('--rm-waypoint-glow', `${this.waypointGlow}`);
      this.root.style.setProperty('--rm-waypoint-passed-opacity', `${this.waypointPassedOpacity}`);

      this.lessons.forEach((lesson) => {
        const toggle = lesson.querySelector('[data-roadmap-toggle]');
        if (!toggle) return;
        toggle.addEventListener('click', () => this.toggleLesson(lesson));
      });

      this.root.querySelector('[data-roadmap-scroll-start]')?.addEventListener('click', () => {
        this.root.querySelector('[data-roadmap-route]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });

      this.bindRevealObserver();
      this.bindLayoutObservers();
      window.addEventListener('scroll', () => this.scheduleScroll(), { passive: true });
      window.addEventListener('resize', () => this.scheduleResponsiveRebuild(), { passive: true });
      window.addEventListener('orientationchange', () => this.scheduleResponsiveRebuild(), { passive: true });
      window.addEventListener('load', () => this.scheduleResponsiveRebuild(), { once: true });
      if (document.fonts?.ready) {
        document.fonts.ready.then(() => this.scheduleResponsiveRebuild());
      }
      this.scheduleResponsiveRebuild();
    }

    toggleLesson(lesson) {
      if (lesson.classList.contains('is-open')) {
        this.closeLesson(lesson);
        return;
      }
      this.lessons.forEach((item) => {
        if (item !== lesson && item.classList.contains('is-open')) this.closeLesson(item);
      });
      this.openLesson(lesson);
    }

    openLesson(lesson) {
      const panel = lesson.querySelector('[data-roadmap-panel]');
      const toggle = lesson.querySelector('[data-roadmap-toggle]');
      if (!panel) return;
      lesson.classList.add('is-open');
      toggle?.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      const height = panel.scrollHeight;
      panel.style.height = '0px';
      panel.style.opacity = '0';
      panel.style.transform = 'translateY(-12px)';
      requestAnimationFrame(() => {
        panel.style.transition = 'height 700ms cubic-bezier(.16,1,.3,1), opacity 420ms ease, transform 700ms cubic-bezier(.16,1,.3,1)';
        panel.style.height = `${height}px`;
        panel.style.opacity = '1';
        panel.style.transform = 'translateY(0)';
      });
      const finish = () => {
        panel.style.height = 'auto';
        panel.removeEventListener('transitionend', finish);
        this.scheduleBuild();
      };
      panel.addEventListener('transitionend', finish);
    }

    closeLesson(lesson) {
      const panel = lesson.querySelector('[data-roadmap-panel]');
      const toggle = lesson.querySelector('[data-roadmap-toggle]');
      if (!panel || panel.hidden) return;
      lesson.classList.remove('is-open');
      toggle?.setAttribute('aria-expanded', 'false');
      panel.style.height = `${panel.scrollHeight}px`;
      panel.style.opacity = '1';
      requestAnimationFrame(() => {
        panel.style.transition = 'height 520ms cubic-bezier(.7,0,.3,1), opacity 260ms ease, transform 520ms cubic-bezier(.7,0,.3,1)';
        panel.style.height = '0px';
        panel.style.opacity = '0';
        panel.style.transform = 'translateY(-8px)';
      });
      const finish = () => {
        panel.hidden = true;
        panel.removeAttribute('style');
        panel.removeEventListener('transitionend', finish);
        this.scheduleBuild();
      };
      panel.addEventListener('transitionend', finish);
    }

    bindRevealObserver() {
      if (!('IntersectionObserver' in window) || this.reducedMotion) {
        this.root.querySelectorAll('[data-roadmap-reveal], [data-roadmap-lesson]').forEach((el) => el.classList.add('is-visible'));
        return;
      }
      const reveal = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('is-visible');
        });
      }, { threshold: .12 });
      this.root.querySelectorAll('[data-roadmap-reveal], [data-roadmap-lesson]').forEach((el) => reveal.observe(el));
    }

    bindLayoutObservers() {
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.scheduleBuild());
        this.resizeObserver.observe(this.root);
      }
      this.root.querySelectorAll('img').forEach((img) => {
        if (!img.complete) img.addEventListener('load', () => this.scheduleBuild(), { once: true });
      });
    }

    normalize(x, y) {
      const magnitude = Math.hypot(x, y) || 1;
      return { x: x / magnitude, y: y / magnitude };
    }

    waypointGeometry(element, index) {
      const rootRect = this.root.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      return {
        element,
        // Geometry lock: the path MUST use the real visual centre of the waypoint.
        // Organic movement belongs in the curve handles, never in the anchor itself.
        x: elementRect.left - rootRect.left + elementRect.width / 2,
        y: elementRect.top - rootRect.top + elementRect.height / 2,
        radius: Math.max(elementRect.width, elementRect.height) / 2 + Math.max(this.threadWidth, this.shadowWidth) / 2 + 1
      };
    }

    finaleSegmentPath(current, next) {
      const rootRect = this.root.getBoundingClientRect();
      const finale = this.finalScene;
      const copy = finale?.querySelector('.trybb-roadmap__passport-copy');
      if (!finale || !copy) return null;

      const finaleRect = finale.getBoundingClientRect();
      const copyRect = copy.getBoundingClientRect();
      const rootWidth = this.root.clientWidth;

      const segmentDirection = this.normalize(next.x - current.x, next.y - current.y);
      const start = {
        x: current.x + segmentDirection.x * current.radius,
        y: current.y + segmentDirection.y * current.radius
      };
      const end = {
        x: next.x - segmentDirection.x * next.radius,
        y: next.y - segmentDirection.y * next.radius
      };

      // Route around the readable quote zone. The corridor sits to the RIGHT
      // of the copy block, but remains inside the full-bleed finale image.
      const copyRight = copyRect.right - rootRect.left;
      const finaleRight = finaleRect.right - rootRect.left;
      const safeRight = Math.min(finaleRight - Math.max(56, rootWidth * .035), rootWidth - 48);
      const corridorX = Math.max(copyRight + Math.max(54, rootWidth * .035), Math.min(safeRight, rootWidth * .76));

      const finaleTop = finaleRect.top - rootRect.top;
      const copyTop = copyRect.top - rootRect.top;
      const copyBottom = copyRect.bottom - rootRect.top;
      const entryY = Math.max(finaleTop + 54, Math.min(copyTop - 38, start.y + 120));
      const exitY = Math.min(end.y - 92, copyBottom + Math.max(52, (end.y - copyBottom) * .24));

      // One SVG path, two continuous cubic curves. No intermediate waypoint,
      // therefore no visual cut or cap between finale sections.
      const d = [
        `M ${start.x.toFixed(2)} ${start.y.toFixed(2)}`,
        `C ${(start.x + (corridorX - start.x) * .42).toFixed(2)} ${(start.y + 48).toFixed(2)}, ${corridorX.toFixed(2)} ${(entryY - 42).toFixed(2)}, ${corridorX.toFixed(2)} ${entryY.toFixed(2)}`,
        `C ${corridorX.toFixed(2)} ${(exitY - 20).toFixed(2)}, ${(end.x + (corridorX - end.x) * .34).toFixed(2)} ${(end.y - 82).toFixed(2)}, ${end.x.toFixed(2)} ${end.y.toFixed(2)}`
      ].join(' ');

      return { startY: start.y, endY: end.y, d };
    }

    threadSegmentsThrough(points) {
      if (points.length < 2) return [];
      const output = [];
      for (let index = 0; index < points.length - 1; index += 1) {
        const previous = points[index - 1] || points[index];
        const current = points[index];
        const next = points[index + 1];
        const after = points[index + 2] || next;

        // The last visible route is a single continuous finale path. It is
        // calculated from the actual DOM geometry and steers around copy.
        if (index === points.length - 2) {
          const finaleDefinition = this.finaleSegmentPath(current, next);
          if (finaleDefinition) {
            output.push(finaleDefinition);
            continue;
          }
        }

        const segmentDirection = this.normalize(next.x - current.x, next.y - current.y);
        const incomingTangent = this.normalize(next.x - previous.x, next.y - previous.y);
        const outgoingTangent = this.normalize(after.x - current.x, after.y - current.y);

        const start = {
          x: current.x + segmentDirection.x * current.radius,
          y: current.y + segmentDirection.y * current.radius
        };
        const end = {
          x: next.x - segmentDirection.x * next.radius,
          y: next.y - segmentDirection.y * next.radius
        };

        const distance = Math.hypot(end.x - start.x, end.y - start.y);
        const handle = Math.max(28, distance * (.27 + Math.sin((index + 1) * 2.13) * .018));
        // Organic variation affects only the curve handles, never waypoint anchors.
        const wave = Math.sin((index + 1) * 1.71) * this.organicVariation;
        const c1 = {
          x: start.x + incomingTangent.x * handle + wave,
          y: start.y + incomingTangent.y * handle
        };
        const c2 = {
          x: end.x - outgoingTangent.x * handle - wave,
          y: end.y - outgoingTangent.y * handle
        };

        output.push({
          startY: start.y,
          endY: end.y,
          d: `M ${start.x.toFixed(2)} ${start.y.toFixed(2)} C ${c1.x.toFixed(2)} ${c1.y.toFixed(2)}, ${c2.x.toFixed(2)} ${c2.y.toFixed(2)}, ${end.x.toFixed(2)} ${end.y.toFixed(2)}`
        });
      }
      return output;
    }

    createPath(className, d) {
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('class', className);
      path.setAttribute('d', d);
      return path;
    }

    buildPath() {
      this.buildFrame = 0;
      if (!this.svg || !this.lineGroup || !this.shadowGroup || this.waypoints.length < 2) return;

      const width = this.root.clientWidth;
      const height = this.root.scrollHeight;
      this.svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
      this.svg.setAttribute('width', width);
      this.svg.setAttribute('height', height);
      this.lineGroup.replaceChildren();
      this.shadowGroup.replaceChildren();
      this.svg.querySelector('defs[data-roadmap-thread-defs]')?.remove();

      const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
      defs.setAttribute('data-roadmap-thread-defs', '');
      this.svg.insertBefore(defs, this.svg.firstChild);
      this.segments = [];
      this.totalLength = 0;

      const definitions = this.threadSegmentsThrough(this.waypoints.map((waypoint, index) => this.waypointGeometry(waypoint, index)));
      definitions.forEach((definition, index) => {
        const isBridgeFromRoots = index === 0;
        const modifier = isBridgeFromRoots ? ' is-road-from-roots' : '';
        const shadowPath = this.createPath(`trybb-roadmap__thread-shadow${modifier}`, definition.d);
        const linePath = this.createPath(`trybb-roadmap__thread-line${modifier}`, definition.d);
        this.shadowGroup.appendChild(shadowPath);
        this.lineGroup.appendChild(linePath);

        const segmentLength = linePath.getTotalLength();
        let revealPaths = [linePath, shadowPath];

        // Patterned bridge needs a separate reveal mask so its dash pattern remains intact.
        if (isBridgeFromRoots) {
          const maskId = `trybb-roadmap-bridge-${this.root.id || 'roadmap'}-${index}`.replace(/[^a-zA-Z0-9_-]/g, '-');
          const mask = document.createElementNS('http://www.w3.org/2000/svg', 'mask');
          mask.setAttribute('id', maskId);
          mask.setAttribute('maskUnits', 'userSpaceOnUse');
          mask.setAttribute('x', '0');
          mask.setAttribute('y', '0');
          mask.setAttribute('width', String(width));
          mask.setAttribute('height', String(height));

          const revealPath = this.createPath('trybb-roadmap__thread-mask-reveal', definition.d);
          revealPath.setAttribute('fill', 'none');
          revealPath.setAttribute('stroke', '#fff');
          revealPath.setAttribute('stroke-width', String(Math.max(this.shadowWidth + 6, this.threadWidth + 8)));
          revealPath.setAttribute('stroke-linecap', 'round');
          revealPath.style.strokeDasharray = `${segmentLength}`;
          revealPath.style.strokeDashoffset = `${segmentLength}`;
          mask.appendChild(revealPath);
          defs.appendChild(mask);
          linePath.setAttribute('mask', `url(#${maskId})`);
          shadowPath.setAttribute('mask', `url(#${maskId})`);
          revealPaths = [revealPath];
        } else {
          revealPaths.forEach((path) => {
            path.style.strokeDasharray = `${segmentLength}`;
            path.style.strokeDashoffset = `${segmentLength}`;
          });
        }

        this.segments.push({
          ...definition,
          line: linePath,
          shadow: shadowPath,
          revealPaths,
          length: segmentLength,
          cumulativeStart: this.totalLength
        });
        this.totalLength += segmentLength;
      });

      this.currentReveal = clamp(this.currentReveal, 0, this.totalLength);
      this.targetReveal = clamp(this.targetReveal, 0, this.totalLength);
      this.applyThreadReveal(this.currentReveal);
      this.updateScrollEffects();
    }

    applyThreadReveal(revealedLength) {
      this.segments.forEach((segment) => {
        const localReveal = clamp(revealedLength - segment.cumulativeStart, 0, segment.length);
        const offset = segment.length - localReveal;
        segment.revealPaths.forEach((path) => { path.style.strokeDashoffset = `${offset}`; });
      });
    }

    cumulativeLengthAtY(targetY) {
      if (!this.segments.length) return 0;
      let revealed = 0;
      for (const segment of this.segments) {
        const minY = Math.min(segment.startY, segment.endY);
        const maxY = Math.max(segment.startY, segment.endY);
        if (targetY >= maxY) {
          revealed += segment.length;
          continue;
        }
        if (targetY <= minY) break;
        let low = 0;
        let high = segment.length;
        for (let iteration = 0; iteration < 22; iteration += 1) {
          const middle = (low + high) / 2;
          const point = segment.line.getPointAtLength(middle);
          if (point.y <= targetY) low = middle;
          else high = middle;
        }
        revealed += low;
        break;
      }
      return clamp(revealed, 0, this.totalLength);
    }

    updateScrollEffects() {
      this.scrollFrame = 0;
      if (!this.segments.length) return;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const rect = this.root.getBoundingClientRect();

      // v0.1.16 — Scroll-activated Thread.
      // At the top of the page the route is fully hidden. The normal reveal
      // lead is introduced progressively over the first small scroll gesture,
      // so the dashed OUR ROOTS bridge only starts drawing once the visitor
      // actually moves down the road.
      const scrollDelta = Math.max(0, (window.scrollY || window.pageYOffset || 0) - this.initialScrollY);
      const activation = clamp(scrollDelta / Math.max(140, viewportHeight * .18), 0, 1);

      // In the finale the head of the Thread stays inside the visible viewport
      // (around 82%) rather than being forced ahead of it. Combined with the
      // faster follow below, this keeps the route visibly travelling with the
      // scroll without pre-drawing disconnected finale segments.
      let activeRevealLead = this.revealLead;
      if (this.finalScene) {
        const finaleRect = this.finalScene.getBoundingClientRect();
        const finaleVisible = finaleRect.top < viewportHeight && finaleRect.bottom > 0;
        if (finaleVisible) activeRevealLead = Math.max(activeRevealLead, .82);
      }

      const revealY = clamp(
        -rect.top + viewportHeight * activeRevealLead * activation,
        0,
        this.root.scrollHeight
      );

      this.waypoints.forEach((waypoint) => {
        const waypointRect = waypoint.getBoundingClientRect();
        const centerY = waypointRect.top - rect.top + waypointRect.height / 2;
        const arrivalWindow = Math.max(72, waypointRect.height * 1.45);
        const delta = revealY - centerY;
        waypoint.classList.toggle('is-thread-arriving', Math.abs(delta) <= arrivalWindow);
        waypoint.classList.toggle('is-thread-passed', delta > arrivalWindow);
      });

      this.targetReveal = this.cumulativeLengthAtY(revealY);
      if (this.reducedMotion) {
        this.currentReveal = this.targetReveal;
        this.applyThreadReveal(this.currentReveal);
      } else if (!this.threadFrame) {
        this.threadFrame = requestAnimationFrame(() => this.animateThread());
      }
    }

    animateThread() {
      this.threadFrame = 0;
      const difference = this.targetReveal - this.currentReveal;
      let follow = this.threadFollow;
      if (this.finalScene) {
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const finaleRect = this.finalScene.getBoundingClientRect();
        if (finaleRect.top < viewportHeight && finaleRect.bottom > 0) {
          // The finale follows more tightly than the editorial middle, but
          // remains scroll-driven. No target is forced ahead, so nothing is
          // visible before the visitor actually reaches it.
          follow = Math.max(follow, .64);
        }
      }
      this.currentReveal += difference * follow;
      if (Math.abs(difference) < .18) this.currentReveal = this.targetReveal;
      this.applyThreadReveal(this.currentReveal);
      if (Math.abs(this.targetReveal - this.currentReveal) >= .18) {
        this.threadFrame = requestAnimationFrame(() => this.animateThread());
      }
    }

    scheduleResponsiveRebuild() {
      // Responsive layouts can settle over more than one frame (font metrics,
      // media queries, browser chrome/orientation). Re-measure after each stage
      // so the path remains physically attached to the waypoint centres.
      this.scheduleBuild();
      requestAnimationFrame(() => this.scheduleBuild());
      window.setTimeout(() => this.scheduleBuild(), 90);
      window.setTimeout(() => this.scheduleBuild(), 240);
    }

    scheduleBuild() {
      if (this.buildFrame) cancelAnimationFrame(this.buildFrame);
      this.buildFrame = requestAnimationFrame(() => this.buildPath());
    }

    scheduleScroll() {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => this.updateScrollEffects());
    }
  }

  const mount = (scope = document) => scope.querySelectorAll(SELECTOR).forEach((root) => {
    if (!root.__trybbRoadmap) root.__trybbRoadmap = new TrybbRoadmap(root);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount());
  else mount();
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
})();
